<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import * as querystring from 'querystring'
import config from '@/config'
import { LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY } from '@/constants'
import { getAuthXToken, enrollPasskey, checkEnrollmentSession } from '@/services/auth'
import { nrPasskeyRegistrationComplete, nrPasskeyRegistrationStart, nrAbandonPasskeyRegistration } from '@/services/newrelic'
import { router, useRoute } from '../../router'
import { useI18n } from '../../setup/setup-i18n'
import { redirectToUniversalLogin } from '../../services/auth/universal'
import { isAllowedRedirectUri } from '../../services/trusted-device/is-allowed-redirect-uri'
import { PasskeyPurpose, PasskeySetupResult } from '../../types'

const { useState: useUserStoreState, useActions: useUserStoreActions } = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState, useGetters: useLoginDomainStoreGetters } = createNamespacedHelpers('LoginDomain')

const INFO_ICONS = [
  'action-lightning',
  'action-stop-sell-availability',
  'action-lock',
]

// the purposes AuthenticatePage sends back after the login round-trip
const RETURN_HOP_PURPOSES: string[] = [
  PasskeyPurpose.passkeyRegistration,
  PasskeyPurpose.passkeyEnrollment,
  PasskeyPurpose.passkeyEnrollmentCancelled,
]

type InfoItem = {
  icon: string
  title: string
  description: string
}

export default defineComponent({

  name: 'PasskeyEnrollmentPage',

  setup() {

    const { t, locale } = useI18n()

    const { userIdentity } = useUserStoreState(['userIdentity'])

    const { tenant } = useLoginDomainStoreState(['tenant'])

    const { loginHelpUrl, loginHelpUrlLocales, logo } = useLoginDomainStoreGetters(['loginHelpUrl', 'loginHelpUrlLocales', 'logo'])
    const { createPasskey } = useUserStoreActions(['createPasskey'])

    const token = getAuthXToken()

    const currentRoute = useRoute()

    // this is the redirect uri from the product
    const getRedirectUri = (): string => {
      return currentRoute.value?.query.redirectUri as string
    }

    // One definition of "usable": present, and the app's own origin or a trusted https domain
    // (see isAllowedRedirectUri). Used to store it, to honour it, and to gate entry.
    const isUsableRedirectUri = (redirectUri?: string | null): redirectUri is string => {
      return !!redirectUri && isAllowedRedirectUri(redirectUri)
    }

    // A redirectUri on this visit wins (even empty or untrusted); without one only a login return
    // hop may use the stored value, so a stale one can't turn a bare URL into an enrolment
    const resolveRedirectUri = (): string | null => {

      const queryRedirectUri = getRedirectUri()

      if (queryRedirectUri !== undefined) {
        return queryRedirectUri || null
      }

      return RETURN_HOP_PURPOSES.includes(getPurpose()) ? localStorage.getItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY) : null

    }

    const storeRedirectURI = () => {

      const redirectUri = resolveRedirectUri()

      if (isUsableRedirectUri(redirectUri)) {
        localStorage.setItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY, redirectUri)
      } else {
        localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY)
      }

    }

    const getPurpose = (): string => {
      return currentRoute.value?.query.purpose as string
    }

    const getLoginHelpUrl = computed(() => {

      if (!loginHelpUrlLocales.value || !locale) {
        return loginHelpUrl
      }

      return loginHelpUrlLocales.value[locale] || loginHelpUrlLocales.value.en

    })

    const loading = ref(true)

    storeRedirectURI()

    // enrolment v2 feature flag (SS-7511): authx app config decides v2 on/off
    // per deployment (PASSKEY_ENROLLMENT_V2_ENABLED) — no dependency on the
    // product appending `enrollmentV2=true` to the link. Absent (or not
    // 'true') the page behaves exactly as it always has (v1 Universal Login
    // round-trip); a product link that still carries the old `enrollmentV2`
    // query param is simply ignored.
    const enrollmentV2 = computed(() => config?.PASSKEY_ENROLLMENT_V2_ENABLED === 'true')

    const enrolling = ref(false)
    const errorMessages = ref<string[]>([])

    const infoMap = computed<InfoItem[]>(() => INFO_ICONS.map((icon, i) => ({
      icon,
      title: t(`PasskeyEnrollmentPage.infoTitles[${i}]`),
      description: t(`PasskeyEnrollmentPage.infoDescriptions[${i}]`),
    })))

    const getRedirectUrl = () => {

      const redirectUri = localStorage.getItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY)
      localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY)

      // The product-supplied redirectUri is only honoured if it points at the app's own
      // origin or one of the configured trusted domains over https (isAllowedRedirectUri —
      // the same guard the TrustedDevice/MFA flows use). A planted or invalid value
      // (e.g. a phishing URL in the query param) degrades to the app-home fallback below,
      // so a completed enrolment can never redirect the user to an untrusted origin.
      if (isUsableRedirectUri(redirectUri)) {
        return new URL(redirectUri)
      }

      // safety net: onMounted already sends anyone without a usable redirect home
      return new URL(`${window.location.protocol}//${window.location.host}/authenticated`)

    }

    // passkeyId lets the product name the new passkey via admin-api (SS-7298)
    const appendSetupSuccess = (redirectUrl: URL, passkeyId?: string) => {
      redirectUrl.searchParams.append('passkeySetup', PasskeySetupResult.success)
      if (passkeyId) {
        redirectUrl.searchParams.append('passkeyId', passkeyId)
      }
    }

    // v2 success — save the passkey and redirect the user back to the product
    // with the same `passkeySetup=success` contract the v1 flow uses.
    const completeEnrollment = async () => {

      const redirectUrl = getRedirectUrl()
      const response = await createPasskey({ token: token.value })

      if (response.code === 'PasskeyCreated') {
        nrPasskeyRegistrationComplete()
        appendSetupSuccess(redirectUrl, response.passkeyId)
      } else {
        nrAbandonPasskeyRegistration()
      }

      window.location.assign(redirectUrl.toString())

    }

    const handleCreatePasskey = async () => {

      errorMessages.value = []
      enrolling.value = true

      try {

        await enrollPasskey(tenant.value)

        await completeEnrollment()

      } catch (error: any) {

        enrolling.value = false

        if (error?.name === 'PasskeyAlreadyExistError') {
          const redirectUrl = getRedirectUrl()
          redirectUrl.searchParams.append('passkeySetup', PasskeySetupResult.passkeyAlreadyExist)
          nrAbandonPasskeyRegistration()
          window.location.assign(redirectUrl.toString())
          return
        }

        // the user dismissed the browser prompt — send them back to the product without
        // the success flag, mirroring the v1 passkeyEnrollmentCancelled outcome
        // (name-based checks: transpiled Error subclasses can't be relied on for instanceof here)
        if (error?.name === 'NotAllowedError' || error?.name === 'PasskeyCreationCancelledError') {

          nrAbandonPasskeyRegistration()
          window.location.assign(getRedirectUrl().toString())
          return

        }

        errorMessages.value = [error?.message || t('PasskeyEnrollmentPage.errors.default')]

      }

    }

    const handleBack = () => {

      // cancel — back to the product without the success flag
      nrAbandonPasskeyRegistration()
      window.location.assign(getRedirectUrl().toString())

    }

    onMounted(async () => {

      // Direct access: no valid redirect (hand-typed, empty, untrusted or stale) means nothing to
      // enrol for, so go home. Checked here too, so entry doesn't rely on what was stored earlier
      if (!isUsableRedirectUri(localStorage.getItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY))) {
        localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY)
        await router.replace({ name: 'AuthenticatedPage' })
        return
      }

      const purpose = getPurpose()

      if (!userIdentity.value) {
        router.replace({
          name: 'Root',
          query: {
            redirectUri: `${window.location.protocol}//${window.location.host}/authenticate?${querystring.stringify({
              purpose: PasskeyPurpose.passkeyRegistration,
            })}`,
            purpose: PasskeyPurpose.passkeyRegistration,
          },
        })
        return
      }

      // enrolment v2 — the user is authenticated and the product opted in:
      // show the in-page enrolment UI, no Universal Login round-trip.
      // Enrolment creates a durable credential, so only a recently established
      // Auth0 session is accepted — a stale/absent session bounces the user
      // through a fresh login and back (the v2 flag and the product redirectUri
      // are persisted, so they return here in v2 mode with a fresh session).
      if (enrollmentV2.value && purpose === PasskeyPurpose.passkeyRegistration) {

        const sessionValid = await checkEnrollmentSession(tenant.value)

        if (!sessionValid) {

          // Stale/absent session — the gate is fail-closed, so bounce the user
          // through an interactive login to establish a fresh session. A silent
          // checkSession round-trip would not re-authenticate a still-valid-but-
          // stale session and would loop back to this gate forever.
          //
          // Go to the authx login page (not Auth0's Universal Login) with
          // prompt=login, which forces the login form to show (the LoginPage skips
          // its silent SSO when prompt=login is set). The user re-auths via the
          // app's normal methods (password or passkey) — the password fallback
          // matters because the just-enrolled passkey's rpId may not match Auth0's
          // login challenge, so the passkey may not be offered. After login the
          // user is redirected back to /authenticate?purpose=passkey-registration
          // (a registered callback, so the post-login redirect works), which routes
          // back to this page. The product redirectUri (localStorage) survives the
          // round-trip; v2 itself needs nothing persisted — it is app config,
          // re-evaluated on mount — and the fresh session passes the gate.
          router.replace({
            name: 'Root',
            query: {
              redirectUri: `${window.location.protocol}//${window.location.host}/authenticate?purpose=${PasskeyPurpose.passkeyRegistration}`,
              prompt: 'login',
            },
          })
          return

        }

        nrPasskeyRegistrationStart()
        loading.value = false
        return

      }

      try {

        // if the user has not yet enrolled in passkey, we direct them to the universal login page
        // and upon successful enrollment, they will be redirected to the authenticate page with the purpose = passkey-enrollment
        if (purpose === PasskeyPurpose.passkeyRegistration) {

          nrPasskeyRegistrationStart()

          await redirectToUniversalLogin({
            tenant: tenant.value,
            redirectUri: `${window.location.protocol}//${window.location.host}/authenticate`,
            prompt: 'login',
            temporaryMfaSetup: false,
            purpose: PasskeyPurpose.passkeyEnrollment,
            email: userIdentity.value?.email,
            loginHelpUrl: getLoginHelpUrl.value,
            logoUri: logo.value,
            ui_locales: locale,
          })

        // If the user has cancelled passkey enrollment, redirect them back to the product
        } else if (purpose === PasskeyPurpose.passkeyEnrollmentCancelled) {

          const redirectUrl = getRedirectUrl()
          window.location.assign(redirectUrl.toString())

        // If the user has finished passkey enrollment, we create authx passkey record and redirect them back to the product
        } else {

          const redirectUrl = getRedirectUrl()
          const response = await createPasskey({ token: token.value })

          if (response.code === 'PasskeyCreated') {
            nrPasskeyRegistrationComplete()
            appendSetupSuccess(redirectUrl, response.passkeyId)
          } else {
            nrAbandonPasskeyRegistration()
          }

          window.location.assign(redirectUrl.toString())

        }

      } catch {
        nrAbandonPasskeyRegistration()
        router.replace({ name: 'UnknownErrorPage' })
      }

    })

    return {
      loading,
      enrollmentV2,
      enrolling,
      errorMessages,
      infoMap,
      handleCreatePasskey,
      handleBack,
    }

  },

})

</script>

<template>
  <div>
    <div
      v-if="loading"
      class="loading"
    >
      <sm-loader />
    </div>
    <div
      v-else-if="enrollmentV2"
      class="underlay"
    >
      <sm-dialog
        :visible="true"
        :title="$t('PasskeyEnrollmentPage.title')"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :hide-border-on-top="true"
        :show-close="false"
        :show-cancel="false"
        :show-confirm="false"
      >
        <template #default>
          <p>{{ $t('PasskeyEnrollmentPage.subtitle') }}</p>
          <sm-help-card
            v-if="errorMessages.length"
            class="pt-2"
            type="warning"
          >
            <template #header>
              {{ $t('PasskeyEnrollmentPage.errors.header') }}
            </template>
            <template #body>
              {{ errorMessages.join(', ') }}
            </template>
          </sm-help-card>
          <div
            class="content__wrapper"
          >
            <div
              v-for="(info, index) in infoMap"
              :key="index"
              class="flex content"
            >
              <div>
                <sm-icon
                  :name="info.icon"
                  height="16px"
                  width="16px"
                />
              </div>
              <div>
                <span><strong>{{ info.title }}</strong></span>
                <p>{{ info.description }}</p>
              </div>
            </div>
          </div>
          <sm-button
            class="retry-button"
            type="primary"
            :loading="enrolling"
            sm-data-test="create-passkey-button"
            @click="handleCreatePasskey"
          >
            {{ $t('PasskeyEnrollmentPage.setupButton') }}
          </sm-button>
        </template>
        <template #footer>
          <sm-button
            type="text"
            sm-data-test="back-button"
            @click="handleBack"
          >
            {{ $t('PasskeyEnrollmentPage.backButton') }}
          </sm-button>
        </template>
      </sm-dialog>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.loading {
  @apply flex justify-center items-center min-h-screen;
}

.underlay {
  @apply bg-white fixed inset-0 flex items-center justify-center;
}

.retry-button {
  @apply text-center mt-5 w-[320px];
  :deep(.sm-button__content) {
    @apply justify-center;
  }
}

.content {
  flex-direction: row;
  gap: 8px;
  @apply text-left;
}

.content__wrapper {
  @apply mt-5 w-[320px];
}

:deep(.sm-dialog__title) {
  @apply flex justify-center;
}
:deep(.sm-dialog__footer) {
  @apply text-left;
}
:deep(.sm-dialog__body) {
  @apply text-center;
}
:deep(.sm-dialog__content) {
  @apply rounded-sm;
  max-width: 400px !important;
}
</style>
